/** Phase B · statutory supply category picker (options = the country tax pack taxonomy) + treatment badge. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Loader2 } from 'lucide-react';
import { API } from '../../App';

const GROUPS = [['standard', 'Standard rate'], ['reduced', 'Reduced rate'], ['zero_rated', 'Zero-rated'], ['exempt', 'Exempt'], ['varies', 'Depends on state'], ['out_of_scope', 'Out of scope']];
const TONE = { standard: 'bg-slate-100 text-slate-700', reduced: 'bg-sky-100 text-sky-800', zero_rated: 'bg-emerald-100 text-emerald-800', exempt: 'bg-amber-100 text-amber-800', varies: 'bg-violet-100 text-violet-800', out_of_scope: 'bg-slate-100 text-slate-500' };

export const useTaxCategories = () => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/products-services/tax-categories`).then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  return data;
};

export const TreatmentBadge = ({ item, taxLabel = 'VAT', onReclassify, busy }) => {
  const t = item.tax_treatment;
  if (!t) return <Badge variant="outline" className="text-[10px]" data-testid={`tax-treatment-${item.id}`}>No tax category</Badge>;
  const rule = item.tax_rule || {};
  const label = GROUPS.find(([k]) => k === t)?.[1] || t;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" title={`${rule.reasoning || ''}${rule.legal_ref ? ` — ${rule.legal_ref}` : ''}`}>
      <Badge className={`text-[10px] ${TONE[t] || ''}`} variant="secondary" data-testid={`tax-treatment-${item.id}`}>
        {taxLabel}: {label}{rule.rate != null && t !== 'exempt' && t !== 'zero_rated' ? ` ${rule.rate}%` : ''}
      </Badge>
      {rule.source === 'ai' && <Badge variant="outline" className="text-[10px]"><Sparkles className="w-3 h-3 mr-0.5" />AI {Math.round((rule.confidence || 0) * 100)}%</Badge>}
      {rule.needs_review && <Badge className="text-[10px] bg-rose-100 text-rose-700" variant="secondary" data-testid={`tax-needs-review-${item.id}`}>Review</Badge>}
      {onReclassify && (
        <button type="button" onClick={onReclassify} disabled={busy} className="text-[10px] text-[#283F8B] hover:underline" data-testid={`tax-reclassify-${item.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Re-check with AI'}
        </button>
      )}
    </span>
  );
};

export const SupplyCategoryField = ({ value, onChange, categories, taxLabel = 'VAT' }) => {
  const cats = categories?.categories || [];
  const sel = cats.find((c) => c.code === value);
  return (
    <div className="space-y-2" data-testid="supply-category-field">
      <Label>{taxLabel} treatment — statutory supply category</Label>
      <Select value={value || 'AUTO'} onValueChange={(v) => onChange(v === 'AUTO' ? '' : v)}>
        <SelectTrigger data-testid="supply-category-select"><SelectValue placeholder="Let AI classify" /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="AUTO" data-testid="supply-category-option-AUTO"><span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#283F8B]" /> Let AI classify from the {categories?.country || ''} tax pack</span></SelectItem>
          {GROUPS.map(([g, gl]) => {
            const rows = cats.filter((c) => c.group === g);
            return rows.length ? (
              <SelectGroup key={g}><SelectLabel>{gl}</SelectLabel>
                {rows.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`supply-category-option-${c.code}`}>{c.label}</SelectItem>)}
              </SelectGroup>
            ) : null;
          })}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {sel ? <>Treatment: <b>{GROUPS.find(([k]) => k === sel.treatment)?.[1]}</b>{sel.rate != null ? ` · ${sel.rate}%` : ''}{sel.note ? ` · ${sel.note}` : ''}</>
          : 'Leave on AI: unknown items are classified against the country tax pack and the rule is stored so the platform knows them next time.'}
      </p>
    </div>
  );
};

export const reclassifyItem = async (id) => {
  try { const { data } = await axios.post(`${API}/products-services/${id}/classify-tax`); toast.success(`Classified as ${data.tax_treatment}`); return data; }
  catch (e) { toast.error(e?.response?.data?.detail || 'Could not classify'); return null; }
};
